import type { ReportFormat } from '@cobliteam/api-lib';
import type { AcelerometerEvents, DriverDTO, DriverEvents, RoadEvents, SafetyRankingParameters, SpeedEvents, UUID, VehicleDTOV2, VehicleSize } from '@cobliteam/common-types';

import type { FleetEventsConfiguration, SafetyRankingParams, SafetyRankingProductivity, SafetyRankingProductType, SafetyRankingScores } from '../../__generated__/graphql';

export interface SafetyRankingParametersResponse {
  params: SafetyRankingParameters;
  minDistanceInKm: number;
  ignoreDismissedEvents: boolean;
}

export type RawParameters = {
  [K in keyof (DriverEvents & RoadEvents & AcelerometerEvents & SpeedEvents) | 'minDistanceInKm']: number;
} & {
  ignoreDismissedEvents: boolean;
};

export interface SafetyRankingParametersRequest {
  params: RawParameters;
  userId: UUID;
  referenceMonth: string;
}

export interface SafetyRankingScoreDetail {
  eventType: string;
  occurrences: number;
  frequency: number;
  discount: number;
}

export interface SafetyRankingRow {
  rank: number | null;
  vehicle?: VehicleDTOV2;
  driver?: DriverDTO;
  productType: string;
  score?: number;
  variation?: number;
  drivenDistanceInKm: number;
  drivenTimeInMinutes: number;
  kmsPerEvent: number;
  totalEventsCount: number;
  scoreDetail: SafetyRankingScoreDetail[];
}

export interface SafetyRankingResponse {
  count: number;
  lastRankUpdate: string;
  classifiedCount: number;
  unclassifiedCount: number;
  averageFleetScore: number;
  rows: SafetyRankingRow[];
}

export enum RankingAggregationType {
  VEHICLE = 'VEHICLE',
  DRIVER = 'DRIVER',
}
export interface BaseSafetyRankingRequest {
  startDate: string;
  endDate: string;
  aggregationType: RankingAggregationType;
  driverIds?: string[];
  vehicleIds?: string[];
  vehicleGroupIds?: string[];
  vehicleSizes?: VehicleSize[];
  productType?: string[];
  productTypes?: string[];
  tagNames?: string[];
  searchQuery?: string;
  sortColumn?: string;
  sortOrder?: string;
}

export interface PagedSafetyRankingRequest extends BaseSafetyRankingRequest {
  page: number;
  size: number;
}

export interface SafetyRankingReportRequest extends BaseSafetyRankingRequest {
  destinationEmails: string[];
  reportFormat: ReportFormat;
}

export type EventsEnabledResponse = Record<keyof (DriverEvents & RoadEvents & AcelerometerEvents & SpeedEvents), boolean>;

export interface SafetyRankingParametersPageResponse {
  safetyRankingProductivity: SafetyRankingProductivity;
  safetyRankingProductType: SafetyRankingProductType;
  safetyRankingScores: SafetyRankingScores;
  fleetEventsConfiguration: FleetEventsConfiguration;
  safetyRankingParams: SafetyRankingParams;
}

export interface SafetyRankingParametersStats {
  classifiedCount: number;
  unclassifiedCount: number;
  averageFleetScore: number;
}

export interface SafetyRankingDistribution {
  range: string;
  number: number;
  fill: string;
}

export interface SafetyRankingParametersLastUpdate {
  lastUpdate: string;
  user: { fullName: string };
}
